import { formatInTimeZone } from "date-fns-tz";

import { WorkerUsageData, WorkerUsageWithContract } from "./types";
import { DAY_MS } from "./constants";

// The API buckets every day at midnight UTC, so all day arithmetic runs in UTC —
// in local time the labels and the today marker shift a day west of Greenwich.
const TIME_ZONE = "UTC";

export const formatWorkerUsageDate = (date: Date, pattern: string) =>
  formatInTimeZone(date, TIME_ZONE, pattern);

const utcDayIndex = (date: Date) => Math.floor(date.getTime() / DAY_MS);

export const startOfUtcDay = (date: Date) => new Date(utcDayIndex(date) * DAY_MS);

// Exactly at the limit is still within contract — burst only draws down above it.
export const isWorkerUsageAboveContract = (data: WorkerUsageWithContract) =>
  data.percentileToDateValue > data.contractedWorkers;

export const getWorkerUsageCycleDay = (data: WorkerUsageData) =>
  utcDayIndex(data.today) - utcDayIndex(data.cycleStart) + 1;

export const getWorkerUsageCycleTotalDays = (data: WorkerUsageData) =>
  utcDayIndex(data.cycleEnd) - utcDayIndex(data.cycleStart) + 1;

export const getWorkerUsagePeak = (data: WorkerUsageData) =>
  data.daily.reduce((acc, point) => Math.max(acc, point.max), 0);

// Shared by the tile and the chart legend, which name the same number.
export const getReferencePercentileTooltip = (percentile: number) =>
  `Your P${percentile} worker usage for the cycle so far. No contracted limit is shown for this account, so this is for reference only.`;

// The API can answer the limit without the percentile and the other way round, and
// neither half means anything alone.
export const hasWorkerUsageContract = (data: WorkerUsageData): data is WorkerUsageWithContract =>
  data.contractedWorkers !== undefined &&
  data.contractedPercentile !== undefined &&
  data.percentileToDateValue !== undefined;

// Not one private worker connected all cycle. There is no usage to plot and none to
// judge, so the contract has nothing to say yet either.
export const isWorkerUsageEmpty = (data: WorkerUsageData) => getWorkerUsagePeak(data) === 0;

const CAP_HEADROOM_RATIO = 1.25;

// Below this the scale starts handing out half-worker ticks.
const MIN_Y_AXIS_TARGET = 5;

// Deliberately scaled to the reference lines and not to the daily peaks: rare spikes
// would otherwise flatten the lines the page is read for. Bars above the resulting
// cap render clipped, labelled with their real peak.
export const getWorkerUsageYAxisTarget = (data: WorkerUsageData) => {
  const maxSeriesValue = data.daily.reduce(
    (acc, point) => Math.max(acc, point.percentileToDateValue ?? 0),
    0
  );

  // The band holds whatever the plot draws: the percentile series plus, on the
  // full view, the contract line.
  const band = hasWorkerUsageContract(data)
    ? Math.max(data.contractedWorkers, data.percentileToDateValue, maxSeriesValue)
    : Math.max(data.fallbackPercentileValue ?? 0, maxSeriesValue);

  const target = band > 0 ? band * CAP_HEADROOM_RATIO : getWorkerUsagePeak(data);

  return Math.max(target, MIN_Y_AXIS_TARGET);
};
